import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import useEmblaCarousel from 'embla-carousel-react';
import { BookOpen, Lightbulb, FileText, HelpCircle, Camera, ChevronLeft, ChevronRight, GraduationCap, Star, Sparkles } from 'lucide-react';

const HomePage = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: true,
    direction: 'rtl'
  });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const slides = [
    {
      title: 'مرحباً بك في رشيد GPT',
      subtitle: 'منصتك الذكية للتعلم والتفوق',
      description: 'استمتع بتجربة تعليمية فريدة مع الذكاء الاصطناعي',
      image: 'https://static.prod-images.emergentagent.com/jobs/ff957073-136f-45e9-a375-a9956d9cb738/images/996e201baea434cfab2ad31d1784f9bfd6909bc9a7acde426a2a17e7154e8b52.png',
      cta: 'ابدأ التعلم الآن',
      link: '/grades'
    },
    {
      title: 'تعلم بذكاء',
      subtitle: 'أدوات تعليمية متطورة',
      description: 'أوراق عمل، اختبارات، ومصطلحات - كلها بضغطة زر',
      image: 'https://static.prod-images.emergentagent.com/jobs/ff957073-136f-45e9-a375-a9956d9cb738/images/31f3730dc1137948bc5a10c8c77a7c3072f38b89f34d4966cea8286616972a25.png',
      cta: 'استكشف الصفوف',
      link: '/grades'
    },
    {
      title: 'حل أي سؤال بالصورة',
      subtitle: 'فقط صوّر السؤال واحصل على الحل',
      description: 'الذكاء الاصطناعي يشرح لك الحل خطوة بخطوة',
      image: 'https://static.prod-images.emergentagent.com/jobs/ff957073-136f-45e9-a375-a9956d9cb738/images/e2e9a045ec31008d52824719a8f35f34ec6c8a90b67e7c9dc7b1137f06ad471e.png',
      cta: 'جرب الآن',
      link: '/solve-image'
    }
  ];

  const features = [
    {
      icon: <Lightbulb className="w-8 h-8" />,
      title: 'نصائح التعلم',
      description: 'احصل على نصائح ذكية لفهم الدروس بشكل أفضل'
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: 'أوراق العمل',
      description: 'أوراق عمل تفاعلية مع الإجابات النموذجية'
    },
    {
      icon: <HelpCircle className="w-8 h-8" />,
      title: 'الاختبارات',
      description: 'اختبارات قصيرة لقياس فهمك للدروس'
    },
    {
      icon: <Camera className="w-8 h-8" />,
      title: 'حل بالصورة',
      description: 'صوّر أي سؤال واحصل على الحل فوراً'
    }
  ];

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    
    // Auto-play
    const autoplay = setInterval(() => {
      if (emblaApi) emblaApi.scrollNext();
    }, 5000);
    
    return () => {
      clearInterval(autoplay);
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <div className="min-h-screen bg-stone-50">
      <Header />
      
      {/* Hero Slider */}
      <section className="hero-section bg-white" data-testid="hero-slider">
        <div className="hero-bg-shape hero-bg-shape-1"></div>
        <div className="hero-bg-shape hero-bg-shape-2"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {slides.map((slide, index) => (
                <div key={index} className="flex-[0_0_100%] min-w-0">
                  <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
                    {/* Text Content */}
                    <div className="order-2 md:order-1 text-center md:text-start animate-fade-in-up">
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full text-orange-600 font-medium text-sm mb-4">
                        <Sparkles className="w-4 h-4" />
                        {slide.subtitle}
                      </div>
                      <h1 className="text-4xl md:text-5xl lg:text-6xl font-black font-['Tajawal'] text-stone-900 leading-tight mb-4">
                        {slide.title}
                      </h1>
                      <p className="text-lg md:text-xl text-stone-600 mb-8">
                        {slide.description}
                      </p>
                      <Link to={slide.link}>
                        <Button 
                          size="lg" 
                          className="gradient-primary gradient-primary-hover text-white text-lg px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all active:scale-95"
                          data-testid={`hero-cta-${index}`}
                        >
                          {slide.cta}
                          <ChevronLeft className="w-5 h-5 ms-2" />
                        </Button>
                      </Link>
                    </div>
                    
                    {/* Image */}
                    <div className="order-1 md:order-2 flex justify-center">
                      <img 
                        src={slide.image} 
                        alt={slide.title}
                        className="w-64 md:w-80 lg:w-96 h-auto object-contain drop-shadow-2xl"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Slider Controls */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button 
              onClick={scrollNext}
              className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-orange-50 transition-colors"
              data-testid="slider-next"
            >
              <ChevronRight className="w-5 h-5 text-stone-600" />
            </button>
            
            <div className="flex gap-2">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => emblaApi?.scrollTo(index)}
                  className={`slider-dot ${selectedIndex === index ? 'active' : ''}`}
                  data-testid={`slider-dot-${index}`}
                />
              ))}
            </div>
            
            <button 
              onClick={scrollPrev}
              className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-orange-50 transition-colors"
              data-testid="slider-prev"
            >
              <ChevronLeft className="w-5 h-5 text-stone-600" />
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-stone-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-['Tajawal'] text-stone-900 mb-4">
              لماذا رشيد GPT؟
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              أدوات ذكية تساعدك على التعلم بفعالية أكبر
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="bg-white border-stone-100 card-hover cursor-default"
                data-testid={`feature-card-${index}`}
              >
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-orange-50 flex items-center justify-center mx-auto mb-4 text-orange-500">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold font-['Tajawal'] text-stone-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-stone-600">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 gradient-primary" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-2xl bg-white/20 flex items-center justify-center">
              <GraduationCap className="w-10 h-10 text-white" />
            </div>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold font-['Tajawal'] text-white mb-4">
            جاهز للتفوق؟
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-xl mx-auto">
            ابدأ رحلتك التعليمية الآن واستكشف الصفوف والمواد الدراسية
          </p>
          <Link to="/grades">
            <Button 
              size="lg" 
              className="bg-white text-orange-500 hover:bg-orange-50 text-lg px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all active:scale-95"
              data-testid="cta-start-button"
            >
              <Star className="w-5 h-5 me-2" />
              ابدأ الآن
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-stone-900 text-stone-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold text-white font-['Tajawal']">
              رشيد GPT
            </span>
          </div>
          <p className="text-sm">
            © 2024 رشيد GPT. جميع الحقوق محفوظة.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
